import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Save, Plus, Trash2, CheckCircle2, HelpCircle, X } from "lucide-react";
import { useParams, useNavigate } from "react-router-dom";
import { useDespachos } from "@/hooks/useDespachos";
import { toast } from "sonner";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter
} from "@/components/ui/dialog";

import { ClientContacts } from "../components/client/ClientContacts";
import { ClientBilling } from "../components/client/ClientBilling";
import { ClientCompany } from "../components/client/ClientCompany";
import { ClientAdvances } from "../components/client/ClientAdvances";
import { ClientPeriodicPayments } from "../components/client/ClientPeriodicPayments";
import { ClientServices } from "../components/client/ClientServices";
import { ClientFiles } from "../components/client/ClientFiles";
import { ClientTasks } from "../components/client/ClientTasks";
import { ClientProcesses } from "../components/client/ClientProcesses";
import { ClientReceipts } from "../components/client/ClientReceipts";

export const ClientDetailPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { clients, updateClient } = useDespachos();

    // Find client in loaded state
    const client = clients.find(c => c.id === id);

    const [activeSection, setActiveSection] = useState("general");
    const [isGroupsHelpOpen, setIsGroupsHelpOpen] = useState(false);
    const [isNewEmailOpen, setIsNewEmailOpen] = useState(false);
    const [newEmailInput, setNewEmailInput] = useState("");
    const [newGroupInput, setNewGroupInput] = useState("");

    const [formData, setFormData] = useState({
        nombre: "",
        primerApellido: "",
        segundoApellido: "",
        codigoCliente: id || "1",
        tipoCliente: "",
        nit: "",
        grupoCliente: ["DESPACHO"] as string[],
        esEmpresa: false,
        razonSocial: "",
        responsabilidades: {
            granContribuyente: false,
            autorretenedor: true,
            agenteRetencion: false,
            regimenSimple: false,
            noResponsable: false
        },
        nacionalidad: "Colombia",
        fax: "",
        paginaWeb: "",
        email: "",
        phone: "",
        secondaryEmails: [] as string[],
        formaPago: "",
        cuentaBancaria: ""
    });

    const [direccion, setDireccion] = useState("");

    useEffect(() => {
        if (client) {
            let extraData: any = {};
            if (client.address) {
                try {
                    extraData = JSON.parse(client.address);
                } catch (e) {
                    extraData = { direccion: client.address };
                }
            }

            setFormData(prev => ({
                ...prev,
                nombre: client.name || "",
                codigoCliente: client.id,
                email: client.email || "",
                phone: client.phone || "",
                nit: client.tax_id || "",
                primerApellido: extraData.primerApellido || "",
                segundoApellido: extraData.segundoApellido || "",
                tipoCliente: extraData.tipoCliente || "",
                grupoCliente: Array.isArray(extraData.grupoCliente) ? extraData.grupoCliente : ["DESPACHO"],
                esEmpresa: extraData.esEmpresa || false,
                razonSocial: extraData.razonSocial || "",
                responsabilidades: extraData.responsabilidades || {
                    granContribuyente: false,
                    autorretenedor: true,
                    agenteRetencion: false,
                    regimenSimple: false,
                    noResponsable: false
                },
                nacionalidad: extraData.nacionalidad || "Colombia",
                fax: extraData.fax || "",
                paginaWeb: extraData.paginaWeb || "",
                secondaryEmails: Array.isArray(extraData.secondaryEmails) ? extraData.secondaryEmails : [],
                formaPago: extraData.formaPago || "",
                cuentaBancaria: extraData.cuentaBancaria || ""
            }));
            setDireccion(extraData.direccion || "");
        }
    }, [client]);

    const handleSave = async () => {
        if (!client) return;
        let existingExtra: any = {};
        if (client.address) {
            try {
                existingExtra = JSON.parse(client.address);
            } catch (e) {
                existingExtra = {};
            }
        }

        const serializedAddress = JSON.stringify({
            ...existingExtra,
            primerApellido: formData.primerApellido,
            segundoApellido: formData.segundoApellido,
            tipoCliente: formData.tipoCliente,
            grupoCliente: formData.grupoCliente,
            esEmpresa: formData.esEmpresa,
            razonSocial: formData.razonSocial,
            responsabilidades: formData.responsabilidades,
            nacionalidad: formData.nacionalidad,
            fax: formData.fax,
            paginaWeb: formData.paginaWeb,
            direccion: direccion,
            secondaryEmails: formData.secondaryEmails,
            formaPago: formData.formaPago,
            cuentaBancaria: formData.cuentaBancaria
        });

        await updateClient(client.id, {
            name: formData.nombre,
            email: formData.email,
            phone: formData.phone,
            tax_id: formData.nit,
            address: serializedAddress
        });

        toast.success("Cambios guardados exitosamente", {
            description: "Los datos del cliente han sido actualizados en la base de datos."
        });
    };

    const handleAddGroup = () => {
        const trimmed = newGroupInput.trim();
        if (trimmed && !formData.grupoCliente.includes(trimmed)) {
            setFormData(prev => ({ ...prev, grupoCliente: [...prev.grupoCliente, trimmed] }));
            setNewGroupInput("");
        }
    };

    const handleRemoveGroup = (tagToRemove: string) => {
        setFormData(prev => ({
            ...prev,
            grupoCliente: prev.grupoCliente.filter(tag => tag !== tagToRemove)
        }));
    };

    const handleAddEmail = () => {
        const emailToSave = newEmailInput.trim().toLowerCase();
        if (!emailToSave) return;

        // Basic validation
        if (!emailToSave.includes("@")) {
            toast.error("Correo no válido", { description: "Por favor ingrese una dirección de correo válida." });
            return;
        }

        if (!formData.email) {
            // Set as primary if no primary email exists
            setFormData(prev => ({ ...prev, email: emailToSave }));
        } else if (!formData.secondaryEmails.includes(emailToSave) && emailToSave !== formData.email) {
            setFormData(prev => ({ ...prev, secondaryEmails: [...prev.secondaryEmails, emailToSave] }));
        }

        setNewEmailInput("");
        setIsNewEmailOpen(false);
        toast.success("Email añadido", { description: `El correo ${emailToSave} fue añadido al expediente del cliente.` });
    };

    const handleRemoveSecondaryEmail = (emailToRemove: string) => {
        setFormData(prev => ({
            ...prev,
            secondaryEmails: prev.secondaryEmails.filter(e => e !== emailToRemove)
        }));
        toast.success("Email eliminado");
    };

    const handleSetPrimaryEmail = (newPrimary: string) => {
        const oldPrimary = formData.email;
        const updatedSecondary = formData.secondaryEmails.filter(e => e !== newPrimary);
        if (oldPrimary && !updatedSecondary.includes(oldPrimary)) {
            updatedSecondary.push(oldPrimary);
        }
        setFormData(prev => ({
            ...prev,
            email: newPrimary,
            secondaryEmails: updatedSecondary
        }));
        toast.success("Email principal actualizado", { description: `${newPrimary} es ahora el correo principal.` });
    };

    const menuItems = [
        { id: "general", label: "INFORMACIÓN GENERAL" },
        { id: "contactos", label: "CONTACTOS" },
        { id: "cobro", label: "DATOS DEL COBRO" },
        { id: "empresa", label: "DATOS DE EMPRESA" },
        { id: "adelantos", label: "ADELANTOS" },
        { id: "pagos", label: "PAGOS PERIÓDICOS" },
        { id: "servicios", label: "SERVICIOS" },
        { id: "archivos", label: "ARCHIVOS" },
        { id: "tareas", label: "TAREAS" },
        { id: "procesos", label: "PROCESOS" },
        { id: "recibos", label: "LISTADO RECIBOS" },
    ];

    return (
        <div className="flex flex-col h-full bg-gray-50/30">
            {/* Header */}
            <div className="flex items-center justify-between p-4 bg-white border-b border-gray-100">
                <div className="flex items-center gap-3">
                    <Button variant="ghost" size="icon" className="rounded-xl hover:bg-gray-100" onClick={() => navigate("/dashboard/despachos/clientes")}>
                        <ArrowLeft className="h-5 w-5 text-gray-400" />
                    </Button>
                    <h2 className="text-xl font-bold text-gray-900">
                        {formData.nombre || "Cliente"} {formData.primerApellido}
                    </h2>
                </div>
                <Button className="rounded-xl bg-gradient-to-r from-legal-blue to-legal-navy hover:from-legal-navy hover:to-legal-blue shadow-md shadow-legal-blue/15" onClick={handleSave}>
                    <Save className="mr-2 h-4 w-4" /> Guardar Cambios
                </Button>
            </div>

            <div className="flex flex-1 overflow-hidden">
                {/* Internal Sidebar */}
                <div className="w-60 bg-white border-r border-gray-100 overflow-y-auto hidden md:block">
                    <div className="py-3 px-2">
                        {menuItems.map(item => (
                            <button
                                key={item.id}
                                onClick={() => setActiveSection(item.id)}
                                className={`w-full text-left px-4 py-2.5 text-xs font-medium rounded-xl mb-0.5 transition-all duration-200 ${activeSection === item.id
                                    ? "bg-legal-blue text-white shadow-md shadow-legal-blue/15"
                                    : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                                    }`}
                            >
                                {item.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Content Area */}
                <div className="flex-1 overflow-y-auto p-6 md:p-8">
                    {activeSection === "general" && (
                        <div className="max-w-4xl space-y-8">

                            {/* Datos Obligatorios Panel */}
                            <Card className="border border-gray-100/80 shadow-sm">
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-base font-semibold text-gray-900">Datos Obligatorios</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-6 pt-4">
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div className="space-y-2">
                                            <Label>Nombre</Label>
                                            <Input
                                                value={formData.nombre}
                                                onChange={e => setFormData({ ...formData, nombre: e.target.value })}
                                                placeholder="XXXX"
                                                className="bg-white"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Primer Apellido</Label>
                                            <Input
                                                value={formData.primerApellido}
                                                onChange={e => setFormData({ ...formData, primerApellido: e.target.value })}
                                                placeholder="XXXX"
                                                className="bg-white"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Segundo Apellido</Label>
                                            <div className="flex gap-2">
                                                <Input
                                                    value={formData.segundoApellido}
                                                    onChange={e => setFormData({ ...formData, segundoApellido: e.target.value })}
                                                    placeholder="XXXX"
                                                    className="bg-white"
                                                />
                                                <Button size="icon" variant="outline" className="shrink-0"><span className="text-gray-400">...</span></Button>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                                        <div className="space-y-2">
                                            <Label>Código Cliente</Label>
                                            <Input value={formData.codigoCliente} className="bg-white" readOnly />
                                        </div>
                                        <div className="space-y-2">
                                            <div className="flex items-center justify-between">
                                                <Label>Tipo de Cliente</Label>
                                                <Button size="sm" variant="ghost" className="h-5 w-5 p-0 text-green-600"><Plus className="h-4 w-4" /></Button>
                                            </div>
                                            <Select
                                                value={formData.tipoCliente}
                                                onValueChange={(val) => setFormData({ ...formData, tipoCliente: val, esEmpresa: val === 'juridica' })}
                                            >
                                                <SelectTrigger className="bg-white"><SelectValue placeholder="Seleccione" /></SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="natural">Persona Natural</SelectItem>
                                                    <SelectItem value="juridica">Persona Jurídica</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="space-y-2">
                                            <Label>NIT/CC</Label>
                                            <Input
                                                value={formData.nit}
                                                onChange={e => setFormData({ ...formData, nit: e.target.value })}
                                                placeholder="8000..."
                                                className="bg-white"
                                            />
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <Label className="flex items-center gap-2">
                                            Grupo del cliente{" "}
                                            <button
                                                type="button"
                                                onClick={() => setIsGroupsHelpOpen(true)}
                                                className="text-blue-600 text-xs font-medium hover:underline inline-flex items-center gap-1 bg-blue-50 px-2 py-0.5 rounded-full transition-colors"
                                            >
                                                <HelpCircle className="h-3 w-3 text-blue-500" /> ¿Para qué sirven los grupos?
                                            </button>
                                        </Label>
                                        <div className="border rounded-xl p-2 bg-white min-h-[44px] flex flex-wrap items-center gap-2 focus-within:ring-2 focus-within:ring-legal-blue/20 transition-all">
                                            {formData.grupoCliente.map(tag => (
                                                <span key={tag} className="bg-gray-100 text-gray-700 font-medium px-2.5 py-1 rounded-lg text-xs flex items-center gap-1.5 border border-gray-200">
                                                    <span>{tag}</span>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveGroup(tag)}
                                                        className="hover:text-red-600 transition-colors"
                                                    >
                                                        <X className="h-3 w-3" />
                                                    </button>
                                                </span>
                                            ))}
                                            <input
                                                className="outline-none text-sm flex-1 min-w-[140px] px-1 py-0.5 bg-transparent"
                                                placeholder="Escriba un grupo y presione Enter..."
                                                value={newGroupInput}
                                                onChange={e => setNewGroupInput(e.target.value)}
                                                onKeyDown={e => {
                                                    if (e.key === "Enter") {
                                                        e.preventDefault();
                                                        handleAddGroup();
                                                    }
                                                }}
                                            />
                                            {newGroupInput.trim() && (
                                                <Button size="sm" variant="ghost" className="h-7 text-xs px-2 text-blue-600" onClick={handleAddGroup}>
                                                    <Plus className="h-3 w-3 mr-1" /> Añadir
                                                </Button>
                                            )}
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-[auto_1fr] gap-4 items-center bg-gray-50/50 p-4 rounded-md border">
                                        <div className="space-y-1">
                                            <Label>Es empresa</Label>
                                            <div className="flex items-center">
                                                <Switch
                                                    checked={formData.esEmpresa}
                                                    onCheckedChange={(c) => setFormData({ ...formData, esEmpresa: c })}
                                                />
                                            </div>
                                        </div>
                                        <div className="space-y-1 w-full">
                                            <Label>Razón Social</Label>
                                            <Input
                                                value={formData.razonSocial}
                                                onChange={e => setFormData({ ...formData, razonSocial: e.target.value })}
                                                placeholder="ALCALDIA BOGOTA"
                                                className="bg-white w-full"
                                                disabled={!formData.esEmpresa}
                                            />
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <Label>Email Principal</Label>
                                        <Input
                                            value={formData.email}
                                            onChange={e => setFormData({ ...formData, email: e.target.value })}
                                            placeholder="correo@ejemplo.com"
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <Label>Teléfono</Label>
                                        <Input
                                            value={formData.phone}
                                            onChange={e => setFormData({ ...formData, phone: e.target.value })}
                                        />
                                    </div>

                                    <div className="space-y-3">
                                        <Label>Responsabilidades RUT</Label>
                                        <div className="flex flex-wrap gap-6">
                                            <div className="flex items-center space-x-2">
                                                <Checkbox id="granContribuyente" />
                                                <label htmlFor="granContribuyente" className="text-sm text-gray-600">Gran Contribuyente</label>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <Checkbox id="autorretenedor" defaultChecked />
                                                <label htmlFor="autorretenedor" className="text-sm text-gray-600">Autorretenedor</label>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <Checkbox id="agenteRetencion" />
                                                <label htmlFor="agenteRetencion" className="text-sm text-gray-600">Agente de retención IVA</label>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <Checkbox id="regimenSimple" />
                                                <label htmlFor="regimenSimple" className="text-sm text-gray-600">Régimen simple de tributación</label>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <Checkbox id="noResponsable" />
                                                <label htmlFor="noResponsable" className="text-sm text-gray-600">No responsable</label>
                                            </div>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Personal Information */}
                            <div className="space-y-4">
                                <h3 className="text-base font-semibold text-gray-900 border-b border-gray-100 pb-2">Personal</h3>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="space-y-2">
                                        <Label>Nacionalidad</Label>
                                        <Select defaultValue="colombia">
                                            <SelectTrigger><SelectValue placeholder="Seleccione" /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="colombia">Colombia</SelectItem>
                                                <SelectItem value="australia">Australia</SelectItem>
                                                <SelectItem value="usa">Estados Unidos</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Fax</Label>
                                        <Input placeholder="XXXX" className="bg-white" />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Pagina web</Label>
                                        <Input placeholder="XXXXX" className="bg-white" />
                                    </div>
                                </div>
                            </div>

                            {/* Emails */}
                            <div className="space-y-4">
                                <div className="flex items-center justify-between border-b pb-2">
                                    <h3 className="text-base font-semibold text-gray-900">Emails del Cliente</h3>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        className="text-green-600 border-green-600 hover:bg-green-50 rounded-xl"
                                        onClick={() => setIsNewEmailOpen(true)}
                                    >
                                        <Plus className="h-4 w-4 mr-1" /> Nuevo Email
                                    </Button>
                                </div>
                                <div className="border rounded-xl bg-white overflow-hidden shadow-sm">
                                    <div className="grid grid-cols-[1fr_120px_120px] gap-4 p-3 font-semibold text-xs text-gray-500 uppercase tracking-wider border-b bg-gray-50/80">
                                        <div>Email</div>
                                        <div className="text-center">Tipo</div>
                                        <div className="text-right">Acciones</div>
                                    </div>

                                    {(!formData.email && formData.secondaryEmails.length === 0) ? (
                                        <div className="p-8 text-center text-gray-500 text-sm">
                                            No hay correos registrados. Haga clic en <strong>"Nuevo Email"</strong> para agregar uno.
                                        </div>
                                    ) : (
                                        <div className="divide-y divide-gray-100">
                                            {formData.email && (
                                                <div className="grid grid-cols-[1fr_120px_120px] gap-4 p-3 items-center text-sm hover:bg-gray-50/50">
                                                    <div className="font-medium text-gray-900 flex items-center gap-2 truncate">
                                                        <span>{formData.email}</span>
                                                    </div>
                                                    <div className="text-center">
                                                        <span className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs font-semibold px-2.5 py-0.5 rounded-full inline-flex items-center gap-1">
                                                            <CheckCircle2 className="h-3 w-3" /> Principal
                                                        </span>
                                                    </div>
                                                    <div className="text-right text-xs text-gray-400 font-medium">
                                                        Dominante
                                                    </div>
                                                </div>
                                            )}
                                            {formData.secondaryEmails.map((secEmail) => (
                                                <div key={secEmail} className="grid grid-cols-[1fr_120px_120px] gap-4 p-3 items-center text-sm hover:bg-gray-50/50">
                                                    <div className="text-gray-700 truncate">{secEmail}</div>
                                                    <div className="text-center">
                                                        <span className="bg-gray-100 text-gray-600 text-xs font-medium px-2.5 py-0.5 rounded-full">
                                                            Secundario
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center justify-end gap-1">
                                                        <Button
                                                            size="sm"
                                                            variant="ghost"
                                                            className="h-7 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50 px-2"
                                                            title="Establecer como principal"
                                                            onClick={() => handleSetPrimaryEmail(secEmail)}
                                                        >
                                                            Hacer Principal
                                                        </Button>
                                                        <Button
                                                            size="icon"
                                                            variant="ghost"
                                                            className="h-7 w-7 text-gray-400 hover:text-red-600 hover:bg-red-50"
                                                            title="Eliminar email"
                                                            onClick={() => handleRemoveSecondaryEmail(secEmail)}
                                                        >
                                                            <Trash2 className="h-3.5 w-3.5" />
                                                        </Button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                        </div>
                    )}

                    {activeSection === "contactos" && <ClientContacts client={client} formData={formData} />}
                    {activeSection === "cobro" && <ClientBilling client={client} formData={formData} setFormData={setFormData} />}
                    {activeSection === "empresa" && <ClientCompany client={client} formData={formData} setFormData={setFormData} />}
                    {activeSection === "adelantos" && <ClientAdvances client={client} />}
                    {activeSection === "pagos" && <ClientPeriodicPayments client={client} />}
                    {activeSection === "servicios" && <ClientServices client={client} />}
                    {activeSection === "archivos" && <ClientFiles client={client} />}
                    {activeSection === "tareas" && <ClientTasks client={client} />}
                    {activeSection === "procesos" && <ClientProcesses client={client} />}
                    {activeSection === "recibos" && <ClientReceipts client={client} />}
                </div>
            </div>

            {/* Modal ¿Para qué sirven los grupos? */}
            <Dialog open={isGroupsHelpOpen} onOpenChange={setIsGroupsHelpOpen}>
                <DialogContent className="sm:max-w-md rounded-2xl">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-lg font-bold text-gray-900">
                            <HelpCircle className="h-5 w-5 text-legal-blue" />
                            ¿Para qué sirven los grupos de clientes?
                        </DialogTitle>
                        <DialogDescription className="text-sm text-gray-600 pt-2 leading-relaxed">
                            Los <strong>grupos de clientes</strong> permiten categorizar y segmentar la información de su firma o despacho legal.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 py-2 text-sm text-gray-600">
                        <div className="bg-blue-50/60 p-3 rounded-xl border border-blue-100 space-y-1.5">
                            <h4 className="font-semibold text-legal-blue text-xs uppercase tracking-wider">Principales usos:</h4>
                            <ul className="list-disc list-inside space-y-1 text-xs text-gray-700">
                                <li><strong>Clasificación:</strong> Agrupe sus clientes por etiquetas como <em>DESPACHO</em>, <em>CORPORATIVO</em>, <em>VIP</em>, <em>PENAL</em> o <em>CIVIL</em>.</li>
                                <li><strong>Filtros rápidos:</strong> Localice procesos y trámites filtrando por el grupo asignado al cliente.</li>
                                <li><strong>Segmentación de reportes:</strong> Facilita la generación de reportes financieros y estados de cuenta por tipo de cliente.</li>
                            </ul>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button onClick={() => setIsGroupsHelpOpen(false)} className="w-full sm:w-auto rounded-xl bg-legal-blue">
                            Entendido
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Modal Nuevo Email */}
            <Dialog open={isNewEmailOpen} onOpenChange={setIsNewEmailOpen}>
                <DialogContent className="sm:max-w-md rounded-2xl">
                    <DialogHeader>
                        <DialogTitle className="text-lg font-bold text-gray-900">
                            Agregar Nuevo Correo Electrónico
                        </DialogTitle>
                        <DialogDescription className="text-sm text-gray-500">
                            Ingrese una dirección de correo para asociarla al cliente.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div className="space-y-2">
                            <Label htmlFor="new-email-input">Correo Electrónico</Label>
                            <Input
                                id="new-email-input"
                                type="email"
                                placeholder="ejemplo@cliente.com"
                                value={newEmailInput}
                                onChange={e => setNewEmailInput(e.target.value)}
                                onKeyDown={e => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        handleAddEmail();
                                    }
                                }}
                                autoFocus
                            />
                        </div>
                    </div>
                    <DialogFooter className="gap-2 sm:gap-0">
                        <Button variant="outline" onClick={() => setIsNewEmailOpen(false)} className="rounded-xl">
                            Cancelar
                        </Button>
                        <Button onClick={handleAddEmail} className="rounded-xl bg-legal-blue">
                            Guardar Email
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};
